import axios from 'axios';
import axiosInstance from 'utils/axios';

import { ConnectionTreeModel } from 'api/connectionTree/connectionTree.model';
import { apiEndpoints } from 'apiEndpoints';
import { DocumentInfoModel } from 'models/documents.model';

import {
  DocumentModel,
  DocumentDetailsModel,
  GetDocumentFilters,
  GetDocumentPagination,
  GetDocumentSort,
  RecipientsToNotify,
} from './documents.model';

const getDocuments = async <T>(
  filters?: GetDocumentFilters,
  pagination?: GetDocumentPagination,
  sort?: GetDocumentSort
): Promise<DocumentModel<T>[]> => {
  const response = await axiosInstance.get(apiEndpoints.documents.index, {
    params: { filters: filters, pagination: pagination, sort: sort },
  });
  return response.data?.rows;
};

const getDocumentsID = async <T>(id: string): Promise<DocumentDetailsModel<T>> => {
  const response = await axiosInstance.get(apiEndpoints.documents.id.replace('{id}', id));
  return response.data;
};

const deleteDocumentsID = async (id: string): Promise<void> => {
  const response = await axiosInstance.delete(apiEndpoints.documents.id.replace('{id}', id));
  return response.data;
};

const postDocumentsIDPrivatePublish = async (id: string, documentFile: File): Promise<void> => {
  const response = await axiosInstance.post(
    apiEndpoints.documents.publishPrivate.replace('{id}', id),
    documentFile
  );
  return response.data;
};

const postDocumentsIDPublicPublish = async (id: string): Promise<void> => {
  const response = await axiosInstance.post(
    apiEndpoints.documents.publishPublic.replace('{id}', id)
  );
  return response.data;
};

const getDocumentsIDFile = async (id: string): Promise<string> => {
  const response = await axiosInstance.get(apiEndpoints.documents.file.replace('{id}', id), {
    responseType: 'blob',
  });
  return response.data;
};

const getDocumentsIDTree = async (id: string): Promise<ConnectionTreeModel> => {
  const response = await axiosInstance.get(apiEndpoints.documents.tree.replace('{id}', id));
  return response.data;
};

const deleteDocumentsIDForget = async (id: string): Promise<void> => {
  const response = await axios.delete(apiEndpoints.documents.forget.replace('{id}', id));
  return response.data;
};

// TODO probably wont be used
const getDocumentsIDReauthorize = async (id: string): Promise<void> => {
  const response = await axios.get(apiEndpoints.documents.reauthorize.replace('{id}', id));
  return response.data;
};

// TODO probably wont be used
const postDocumentsIDReauthorize = async (
  id: string,
  recipients: RecipientsToNotify
): Promise<void> => {
  const response = await axios.post(
    apiEndpoints.documents.reauthorize.replace('{id}', id),
    recipients
  );
  return response.data;
};

// TODO probably wont be used
const getDocumentsIDRecipientsFile = async (id: string): Promise<void> => {
  const response = await axios.get(apiEndpoints.documents.recipientsFile.replace('{id}', id));
  return response.data;
};

// TODO probably wont be used
const patchDocumentsIDRecipientsFile = async (id: string, file: File): Promise<void> => {
  const response = await axios.patch(
    apiEndpoints.documents.recipientsFile.replace('{id}', id),
    file
  );
  return response.data;
};

// TODO filters, also probably wont be used
const getDocumentsReportFile = async (): Promise<string> => {
  const response = await axios.get(apiEndpoints.documents.documentsReportFile);
  return response.data;
};

export type PostDocumentProps = {
  documentInfo: DocumentInfoModel;
  documentContent: File;
};

const postDocumentsPrivate = async ({
  documentInfo,
  documentContent,
}: PostDocumentProps): Promise<{}> => {
  const formData = new FormData();
  documentContent && formData.append('documentFile', documentContent);
  formData.append(
    'documentInfo',
    new Blob([JSON.stringify(documentInfo)], { type: 'application/json' })
  );

  const response = await axiosInstance.post(apiEndpoints.documents.private, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return response.data;
};

const postDocumentsPublic = async ({
  documentInfo,
  documentContent,
}: PostDocumentProps): Promise<{}> => {
  const formData = new FormData();
  documentContent && formData.append('documentFile', documentContent);
  formData.append(
    'documentInfo',
    new Blob([JSON.stringify(documentInfo)], { type: 'application/json' })
  );

  return axiosInstance.post(apiEndpoints.documents.public, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

const patchDocumentIDFavourite = async (id: string, favourite: boolean): Promise<void> => {
  const response = await axiosInstance.patch(apiEndpoints.documents.id.replace('{id}', id), {
    favourite,
  });
  return response.data;
};

const documents = {
  getDocuments,
  getDocumentsID,
  deleteDocumentsID,
  postDocumentsIDPrivatePublish,
  postDocumentsIDPublicPublish,
  getDocumentsIDFile,
  getDocumentsIDTree,
  deleteDocumentsIDForget,
  getDocumentsIDReauthorize,
  postDocumentsIDReauthorize,
  getDocumentsIDRecipientsFile,
  patchDocumentsIDRecipientsFile,
  getDocumentsReportFile,
  postDocumentsPrivate,
  postDocumentsPublic,
  patchDocumentIDFavourite,
};

export default documents;
